import React, { useMemo } from 'react';
import {
  SubHeaderProps,
  SubHeaderIdentity,
  SubHeaderMiddleTabs,
  SubHeaderRightActions
} from './subheader/index';
import { useProject } from '../contexts/ProjectContext';
import { useUI } from '../contexts/UIContext';

const SubHeader: React.FC<SubHeaderProps> = (props) => {
  const projectCtx = useProject();
  const uiCtx = useUI();

  const viewMode = props.viewMode ?? uiCtx.viewMode;
  const rawKanbanMode = props.kanbanMode ?? uiCtx.kanbanMode;
  const resolvedKanbanMode = (viewMode === 'globalKanban' && rawKanbanMode === 'custom' ? 'status' : rawKanbanMode) as 'status' | 'date' | 'custom';

  const resolvedProps = {
    ...props,
    viewMode,
    setViewMode: props.setViewMode ?? uiCtx.setViewMode,
    projectName: props.projectName ?? projectCtx.projectName,
    activeProject: props.activeProject !== undefined ? props.activeProject : projectCtx.activeProject,
    isSidebarCollapsed: props.isSidebarCollapsed ?? uiCtx.isSidebarCollapsed,
    isRightSidebarOpen: props.isRightSidebarOpen ?? false,
    isReadOnly: props.isReadOnly ?? false,
    uiMode: props.uiMode ?? uiCtx.uiMode,
    onToggleUiMode: props.onToggleUiMode ?? (() => uiCtx.setUiMode(uiCtx.uiMode === 'expert' ? 'standard' : 'expert')),
    showArchived: props.showArchived ?? uiCtx.showArchived,
    setShowArchived: props.setShowArchived ?? uiCtx.setShowArchived,
    showCompleted: props.showCompleted ?? uiCtx.showCompleted,
    setShowCompleted: props.setShowCompleted ?? uiCtx.setShowCompleted,
    showTaskDetails: props.showTaskDetails ?? uiCtx.showTaskDetails,
    setShowTaskDetails: props.setShowTaskDetails ?? uiCtx.setShowTaskDetails,
    kanbanMode: resolvedKanbanMode,
    setKanbanMode: props.setKanbanMode ?? (uiCtx.setKanbanMode as any),
    communicationTab: props.communicationTab ?? uiCtx.communicationTab,
    setCommunicationTab: props.setCommunicationTab ?? uiCtx.setCommunicationTab,
    calendarDate: props.calendarDate ?? uiCtx.calendarDate,
    setCalendarDate: props.setCalendarDate ?? uiCtx.setCalendarDate,
    calendarViewType: props.calendarViewType ?? uiCtx.calendarViewType,
    setCalendarViewType: props.setCalendarViewType ?? uiCtx.setCalendarViewType,
    calendarFilterMode: props.calendarFilterMode ?? uiCtx.calendarFilterMode,
    setCalendarFilterMode: props.setCalendarFilterMode ?? uiCtx.setCalendarFilterMode,
    globalFilterAssignee: props.globalFilterAssignee ?? (uiCtx.filterAssignee || 'all'),
    setGlobalFilterAssignee: props.setGlobalFilterAssignee ?? uiCtx.setFilterAssignee,
    globalFilterStatus: props.globalFilterStatus ?? (uiCtx.filterStatus || 'all'),
    setGlobalFilterStatus: props.setGlobalFilterStatus ?? uiCtx.setFilterStatus,
    globalFilterType: props.globalFilterType ?? (uiCtx.filterType || 'all'),
    setGlobalFilterType: props.setGlobalFilterType ?? uiCtx.setFilterType,
    showToolsDropdown: props.showToolsDropdown ?? uiCtx.showToolsDropdown,
    setShowToolsDropdown: props.setShowToolsDropdown ?? uiCtx.setShowToolsDropdown,
    setShowDataDropdown: props.setShowDataDropdown ?? uiCtx.setShowDataDropdown,
    handleGenerateReport: props.handleGenerateReport ?? projectCtx.handleGenerateReport,
    handleDownloadRoadmap: props.handleDownloadRoadmap ?? projectCtx.handleDownloadRoadmap,
    exportAsExcel: props.exportAsExcel ?? projectCtx.exportAsExcel,
    exportTaskTableAsExcel: props.exportTaskTableAsExcel ?? projectCtx.exportTaskTableAsExcel,
    setShowAIPlanner: props.setShowAIPlanner ?? uiCtx.setShowAIPlanner,
    setShowProjectManager: props.setShowProjectManager ?? uiCtx.setShowProjectManager,
    searchQuery: props.searchQuery ?? uiCtx.searchQuery,
    setSearchQuery: props.setSearchQuery ?? uiCtx.setSearchQuery,
    showFilters: props.showFilters ?? uiCtx.showFilters,
    setShowFilters: props.setShowFilters ?? uiCtx.setShowFilters,
  };

  const {
    projectName,
    activeProject,
    isSidebarCollapsed,
    isRightSidebarOpen,
    calendarDate,
    isReadOnly,
  } = resolvedProps;

  const isGlobalView = useMemo(() => [
    'globalKanban', 'communication', 'analysis', 'impactEffort',
    'metricsDashboard', 'globalCalendar', 'dashboard', 'aiAssistant', 'globalGantt'
  ].includes(viewMode), [viewMode]);

  const leftOffset = isSidebarCollapsed ? '80px' : '320px';
  const rightOffset = isRightSidebarOpen ? '80px' : '0px';

  return (
    <div
      className="gwantt-subheader bg-white/95 dark:bg-slate-900/95 backdrop-blur-md h-[52px] fixed top-[64px] z-40 flex items-center border-b border-slate-200 dark:border-slate-800 shadow-sm transition-all duration-300"
      style={{
        left: leftOffset,
        right: rightOffset,
        width: `calc(100% - ${leftOffset} - ${rightOffset})`
      }}
    >
      <div className="w-full h-full px-5 flex items-center justify-between relative">
        {/* Left Column: Focus / Identity */}
        <SubHeaderIdentity
          viewMode={viewMode}
          projectName={projectName}
          activeProject={activeProject}
          isReadOnly={isReadOnly}
          calendarDate={calendarDate}
        />

        {/* Middle Column: Tabs & Navigation */}
        <SubHeaderMiddleTabs {...(resolvedProps as any)} />

        {/* Right Column: Filters, Actions, Dropdowns */}
        <SubHeaderRightActions {...(resolvedProps as any)} isGlobalView={isGlobalView} />
      </div>
    </div>
  );
};

export default React.memo(SubHeader);
